import React, { useCallback, useEffect, useRef, useState } from "react";
import { FaBell, FaTimes, FaCheckDouble, FaSearch, FaTrash } from "react-icons/fa";
import notificationApi from "../../services/notificationApi";
import useSocket from "../../hooks/useSocket";
import EmptyState from "../common/EmptyState";
import Skeleton from "../common/Skeleton";
import { useToast } from "../common/Toast";
import "../../styles/notificationCenter.css";

const TYPE_OPTIONS = [
  { value: "", label: "All types" },
  { value: "target_assigned", label: "Target Assigned" },
  { value: "target_achieved", label: "Achievement" },
  { value: "target_completed", label: "Target Completed" },
  { value: "activity", label: "Activity" },
];

/* =========================================================
   NotificationCenter
   Full notification center - replaces the old basic bell
   dropdown (components/NotificationBell.jsx). Supports:
     - live updates via Socket.IO ("notification" / "admin_notification")
     - search, type filter, date range
     - pagination
     - mark single / mark all as read
     - delete
     - toast pop-up on new incoming notification
========================================================= */
function NotificationCenter() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 15;

  const [search, setSearch] = useState("");
  const [type, setType] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const panelRef = useRef(null);
  const toast = useToast();

  const fetchUnread = useCallback(() => {
    notificationApi
      .getUnreadCount()
      .then((res) => setUnreadCount(res.data.count || 0))
      .catch(() => {});
  }, []);

  const fetchList = useCallback(() => {
    setLoading(true);
    notificationApi
      .getNotificationCenter({
        search: search || undefined,
        type: type || undefined,
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
        page,
        limit,
      })
      .then((res) => {
        setItems(res.data.data || []);
        setTotal(res.data.total || 0);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [search, type, dateFrom, dateTo, page]);

  useEffect(() => {
    fetchUnread();
    const interval = setInterval(fetchUnread, 8000);
    return () => clearInterval(interval);
  }, [fetchUnread]);

  useEffect(() => {
    if (open) fetchList();
  }, [open, fetchList]);

  // Live updates
  const handleLive = useCallback(
    (notification) => {
      toast.info(notification.message, notification.title || "New Notification");
      setUnreadCount((c) => c + 1);
      if (open) fetchList();
    },
    [toast, open, fetchList]
  );
  useSocket("notification", handleLive);
  useSocket("admin_notification", handleLive);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const handleItemClick = (n) => {
    if (!n.is_read) {
      notificationApi.markAsRead(n.id).then(() => {
        setItems((prev) => prev.map((i) => (i.id === n.id ? { ...i, is_read: 1 } : i)));
        setUnreadCount((c) => Math.max(0, c - 1));
      });
    }
  };

  const handleDelete = (id, e) => {
    e.stopPropagation();
    notificationApi
      .deleteNotification(id)
      .then(() => {
        setItems((prev) => prev.filter((i) => i.id !== id));
        setTotal((t) => Math.max(0, t - 1));
        toast.success("Notification deleted");
      })
      .catch(() => toast.error("Failed to delete notification"));
  };

  const handleMarkAllRead = () => {
    notificationApi.markAllRead().then(() => {
      setItems((prev) => prev.map((i) => ({ ...i, is_read: 1 })));
      setUnreadCount(0);
      toast.success("All notifications marked as read");
    });
  };

  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <div className="notif-center" ref={panelRef}>
      <button className="notif-bell-btn" onClick={() => setOpen((o) => !o)}>
        <FaBell />
        {unreadCount > 0 && <span className="notif-bell-badge">{unreadCount}</span>}
      </button>

      {open && (
        <div className="notif-panel">
          <div className="notif-panel-header">
            <h4>Notifications</h4>
            <div className="notif-panel-header-actions">
              <button title="Mark all as read" onClick={handleMarkAllRead}>
                <FaCheckDouble />
              </button>
              <button title="Close" onClick={() => setOpen(false)}>
                <FaTimes />
              </button>
            </div>
          </div>

          <div className="notif-filters">
            <div className="notif-search-box">
              <FaSearch />
              <input
                placeholder="Search notifications..."
                value={search}
                onChange={(e) => {
                  setPage(1);
                  setSearch(e.target.value);
                }}
              />
            </div>
            <div className="notif-filter-row">
              <select
                value={type}
                onChange={(e) => {
                  setPage(1);
                  setType(e.target.value);
                }}
              >
                {TYPE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => {
                  setPage(1);
                  setDateFrom(e.target.value);
                }}
              />
              <input
                type="date"
                value={dateTo}
                onChange={(e) => {
                  setPage(1);
                  setDateTo(e.target.value);
                }}
              />
            </div>
          </div>

          <div className="notif-list">
            {loading && <Skeleton.Table rows={4} cols={1} />}

            {!loading && items.length === 0 && (
              <EmptyState icon="🔔" title="No notifications" subtitle="You're all caught up" />
            )}

            {!loading &&
              items.map((n) => (
                <div
                  key={n.id}
                  className={`notif-item ${n.is_read ? "read" : "unread"}`}
                  onClick={() => handleItemClick(n)}
                >
                  <div className="notif-item-top">
                    <span className="notif-type-badge">{n.type || "info"}</span>
                    <span className="notif-time">
                      {new Date(n.created_at).toLocaleString("en-IN")}
                    </span>
                  </div>
                  <div className="notif-message">{n.message}</div>
                  <button className="notif-delete-btn" onClick={(e) => handleDelete(n.id, e)}>
                    <FaTrash />
                  </button>
                </div>
              ))}
          </div>

          {total > limit && (
            <div className="notif-pagination">
              <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                ‹
              </button>
              <span>
                {page} / {totalPages}
              </span>
              <button disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
                ›
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default NotificationCenter;
